import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ChevronDown, Gift, LogOut } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Account dropdown for the header — an initials avatar that opens a small
 * menu (name/email + My gifts + Sign out). Replaces the cramped row of text
 * links on mobile. Accessible: aria-haspopup/expanded, Escape + click-outside
 * to close, focus stays on the trigger.
 */
interface AccountMenuProps {
  displayName: string;
  email: string;
  onSignOut: () => void;
}

export function AccountMenu({ displayName, email, onSignOut }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const initial = (displayName || email || '?').trim().charAt(0).toUpperCase();

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="flex items-center gap-1 rounded-full pl-0.5 pr-1.5 py-0.5 hover:bg-surface-sunken/70 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ink text-bg text-sm font-semibold">
          {initial}
        </span>
        <ChevronDown
          className={cn('w-4 h-4 text-ink-muted transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 rounded-2xl border border-border bg-surface shadow-lg p-1.5 z-[var(--z-dropdown)] origin-top-right"
        >
          <div className="px-3 py-2 mb-1 border-b border-border">
            <p className="text-sm font-medium text-ink truncate">{displayName}</p>
            <p className="text-xs text-ink-faint truncate">{email}</p>
          </div>
          <Link
            to="/dashboard"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm text-ink hover:bg-surface-sunken focus-visible:outline-none focus-visible:bg-surface-sunken"
          >
            <Gift className="w-4 h-4 text-ink-muted" />
            My gifts
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-sm text-ink hover:bg-surface-sunken focus-visible:outline-none focus-visible:bg-surface-sunken"
          >
            <LogOut className="w-4 h-4 text-ink-muted" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
